/**
 * DURMAH — ETHICAL MONETISATION LANGUAGE
 *
 * Reusable, doctrine-aligned strings for marketing and product surfaces.
 *
 * Companion doctrine: docs/brand/ETHICAL_MONETISATION_SYSTEM.md
 *
 * Rules for using these strings:
 *  - These are LOCKED brand strings. Edit only with founder approval.
 *  - Never combine with copy that implies paying unlocks better academics.
 *  - Never use as headlines for paid-tier upsells.
 */

export const MONETISATION_HEADLINES = {
  COMMITMENT: "Our commitment to students",
  EQUALITY: "All students learn equally.",
  FAIRNESS_OVER_ADVANTAGE: "Fairness, not advantage.",
  SUPPORT_NOT_SHORTCUTS: "We sell support, not shortcuts.",
} as const;

export const MONETISATION_TAGLINES = {
  NO_PAY_TO_LEARN_BETTER: "No student pays to learn better.",
  EQUAL_LEARNING_OPTIONAL_EXPERIENCE:
    "All students learn equally. Some choose to experience more.",
  BUILT_ON_FAIRNESS: "Durmah is built on fairness, not advantage.",
  SUPPORT_NOT_SHORTCUTS: "We sell support, not shortcuts.",
} as const;

export const MONETISATION_COMMITMENTS = {
  EQUAL_CAPABILITY:
    "Every subscribed student receives the full academic capability of Durmah.",
  NEVER_GATE_LEARNING:
    "We do not charge for better learning.",
  ONLY_GATE_EXPERIENCE:
    "We charge only for enhanced experience, organisation, and support.",
  NO_GRADE_GUARANTEES:
    "Durmah does not promise better grades. We promise structured practice that helps you become a stronger legal thinker.",
  NO_PREMIUM_ANSWERS:
    "There is no premium tier with better academic answers. Academic capability is the floor, not the ceiling.",
} as const;

/**
 * Negative-space messaging — explicit refusals.
 * Useful in FAQs, ethics pages, and trust-building sections.
 */
export const MONETISATION_REFUSALS = [
  "We do not sell better grades.",
  "We do not sell premium academic answers.",
  "We do not sell exclusive academic advantage.",
  "We do not hide academic features behind paid tiers.",
] as const;

/**
 * What CAN be monetised — for transparency in pricing/philosophy copy.
 * Mirrors the four layers in the doctrine document.
 */
export const PERMITTED_MONETISATION_LAYERS = [
  {
    title: "Experience",
    body: "Voice quality, responsiveness, and interface polish.",
  },
  {
    title: "Personalisation",
    body: "Adaptive pacing, deeper memory, dashboards tailored to you.",
  },
  {
    title: "Productivity",
    body: "Organisation tools, capacity, and convenience around your study.",
  },
  {
    title: "Insight & Support",
    body: "Parent or guardian visibility, progress summaries, wellbeing tooling.",
  },
] as const;

/**
 * Pricing-page commitment block — three-line student promise.
 * Use in `/pricing` Student Commitment section.
 */
export const PRICING_PAGE_COMMITMENT_BULLETS = [
  MONETISATION_COMMITMENTS.EQUAL_CAPABILITY,
  MONETISATION_COMMITMENTS.NEVER_GATE_LEARNING,
  MONETISATION_COMMITMENTS.ONLY_GATE_EXPERIENCE,
] as const;
